import React, { useEffect, useState } from "react";
import moment from "moment";
import Loading from "../Modals/Loading";
import { useLocation, useNavigate } from "react-router-dom";
import quotationService from "../Services/quotationService";
import * as xlsx from "xlsx";
import { IoCreateOutline } from "react-icons/io5";

const ViewQuotation = () => {
  const [quotation, setQuotation] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [showCreateButton, setShowCreateButton] = useState(true);
  const [showDetails, setShowDetails] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  useEffect(() => {
    const getQuotation = async () => {
      setLoading(true);
      try {
        const response = await quotationService.single(params.get("id"));
        setQuotation(response.quotation);

        if (response.quotation.status === "pending") {
          setShowCreateButton(false);
        } else {
          setShowCreateButton(true);
        }
      } catch (e) {
        console.error("Failed to fetch Orders", error);
        setError(true); // to handle errors
      }
      setLoading(false);
    };
    getQuotation();
  }, []);

  const totalQuantity = quotation?.details.reduce(
    (acc, item) => acc + item.quantity,
    0,
  );
  const totalWeight = quotation?.details.reduce(
    (acc, item) => acc + parseFloat(item.weight || 0),
    0,
  );
  const totalPrice = quotation?.details.reduce(
    (acc, item) => acc + item.quantity * parseFloat(item.price),
    0,
  );
  const totalVatPrice = quotation?.details
    .reduce(
      (acc, item) =>
        acc +
        (item.quantity * parseFloat(item.price) +
          (item.vat_percentage * item.quantity * parseFloat(item.price)) / 100),
      0,
    )
    .toFixed(2);

  const downloadQuotation = () => {
    const summary = [
      { Weight: "Total Quantity", Delivery: totalQuantity },
      { Weight: "Total Weight", Delivery: totalWeight },
      { Weight: "Total Price", Delivery: totalPrice.toFixed(2) },
    ];

    const transformedData = quotation?.details.map((item) => ({
      "Vendor Code": item.vendor_code,
      Brand: item.brand,
      Title: item.title,
      Quantity: item.quantity,
      Price: item.price,
      Weight: item.weight,
      Delivery: item.delivery,
    }));

    const blankRows = [{}, {}, {}];
    const fullData = [...transformedData, ...blankRows, ...summary];

    const worksheet = xlsx.utils.json_to_sheet(fullData);

    const workbook = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(workbook, worksheet, "Quotation");

    const excelBuffer = xlsx.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "quotation.xlsx");
    document.body.appendChild(link);
    link.click();

    link.parentNode.removeChild(link);
  };

  return (
    <main className="mx-10 my-2 p-6 rounded-3xl border border-gray-200 shadow-lg bg-white min-h-[calc(100vh-48px)]">
      {loading ? (
        <div className="mt-64">
          <Loading />
        </div>
      ) : quotation ? (
        <>
          <div className="flex flex-row justify-between">
            <h1 className="font-bold text-2xl text-gray-800 animate__animated animate__fadeInLeft">
              Quotation - {quotation.id}
            </h1>
            <button
              onClick={() => downloadQuotation()}
              className="button-primary"
            >
              Download Excel
            </button>
          </div>

          <div className="flex flex-row gap-4">
            <h6 className="flex w-fit text-primary font-bold text-xs tracking-wide bg-gray-100 px-4 py-[5px] rounded-full">
              <pre className="text-gray-500 font-bold">
                Quotation Created At:{" "}
              </pre>
              {moment(quotation?.created_at).format("LL")}
            </h6>
            <h6 className="flex w-fit text-primary font-bold text-xs tracking-wide bg-gray-100 px-4 py-[5px] rounded-full">
              <pre className="text-gray-500 font-bold">Quotation Date: </pre>
              {moment(quotation?.quotation_date).format("LL")}
            </h6>
          </div>
          <div className="flex flex-col bg-white p-6 border rounded-2xl w-[40%] mt-10 shadow-lg">
            <h1 className="font-bold text-xl text-secondary">
              Customer Details
            </h1>
            <div className="mt-4 gap-4 space-y-2">
              <p className="text-sm font-medium text-gray-400">
                Name:{" "}
                <span className="text-gray-800 font-semibold">
                  {quotation.customer}
                </span>
              </p>
              <p className="text-sm font-medium text-gray-400">
                Phone:{" "}
                <span className="text-gray-800 font-semibold">
                  {quotation.phone}
                </span>
              </p>
              <p className="text-sm font-medium text-gray-400">
                Email:{" "}
                <span className="text-gray-800 font-semibold">
                  {quotation.email}
                </span>
              </p>
            </div>
          </div>

          {/* Header with toggle button */}
          <div className="flex flex-row justify-between items-center mt-8">
            <h1 className="font-bold text-xl text-gray-800">
              Quotation Details
            </h1>
            <button
              onClick={() => setShowDetails((prev) => !prev)}
              className="flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 hover:shadow transition-all duration-300"
            >
              <svg
                className={`w-4 h-4 transition-transform duration-300 ${showDetails ? "rotate-180" : ""}`}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
              </svg>
              {showDetails
                ? "Hide Details"
                : `Show Details (${quotation.details?.length} items)`}
            </button>
          </div>

          <div
            className={`transition-all duration-500 overflow-hidden ${showDetails ? "opacity-100 mt-4" : "max-h-0 opacity-0"}`}
          >
            <div className="table-div">
              <table className="w-full">
                <thead>
                  <tr className="border-y-2 text-left w-full text-black text-[10px]">
                    <th className="py-3 pl-4 w-[1%]">Sr No.</th>
                    <th className="py-3 pl-4 w-[1%]">Vendor Code</th>
                    <th className="py-3 pl-4 w-[3%]">Brand</th>
                    <th className="py-3 pl-4 w-[1%]">Weight</th>
                    <th className="py-3 pl-4 w-[3%]">Price</th>
                    <th className="py-3 pl-4 w-[1%]">Quantity</th>
                    <th className="py-3 pl-4 text-center w-[3%]">
                      Total Price Excl VAT
                    </th>
                    <th className="py-3 pl-4 text-center w-[3%]">VAT</th>
                    <th className="py-3 pl-4 text-center w-[3%]">
                      Total Price Incl VAT
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {quotation.details &&
                    quotation.details.map((detail, index) => (
                      <tr
                        key={index}
                        className={`border-b border-gray-100 text-left ${
                          index % 2 === 0 ? "bg-gray-50" : "bg-accent"
                        }`}
                      >
                        <td className="pl-4 w-[1%] py-3 font-bold text-blue-500">
                          {index + 1}.
                        </td>
                        <td className="pl-4 w-[2%] py-3">
                          {detail.vendor_code}
                        </td>
                        <td className="pl-4 w-[3%] py-3 font-bold">
                          {detail.brand}
                        </td>
                        <td className="pl-4 w-[1%] py-3">{detail.weight}</td>
                        <td className="pl-4 w-[2%] py-3">
                          {Number(detail.price).toFixed(2)} AED
                        </td>
                        <td className="pl-4 w-[1%] py-3">{detail.quantity}</td>
                        <td className="pl-4 w-[3%] py-3 text-center">
                          {(detail.quantity * detail.price).toFixed(2)} AED
                        </td>
                        <td className="pl-4 w-[3%] py-3 text-center">
                          {(
                            (detail.vat_percentage *
                              detail.quantity *
                              detail.price) /
                            100
                          ).toFixed(2)}{" "}
                          AED
                        </td>
                        <td className="pl-4 w-[3%] py-3 text-center">
                          {(
                            detail.quantity * detail.price +
                            (detail.vat_percentage *
                              detail.quantity *
                              detail.price) /
                              100
                          ).toFixed(2)}{" "}
                          AED
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex flex-row justify-center gap-4 mt-4 flex-wrap">
            {[
              { label: "Total Items", value: quotation.details?.length },
              { label: "Total Quantity", value: totalQuantity },
              {
                label: "Total Price Excl. VAT",
                value: `${quotation.amount} AED`,
              },
              {
                label: "VAT",
                value: `${(parseFloat(totalVatPrice) - parseFloat(quotation.amount)).toFixed(2)} AED`,
              },
              { label: "Total Price Incl. VAT", value: `${totalVatPrice} AED` },
            ].map((stat) => (
              <div
                key={stat.label}
                className="flex flex-col bg-gray-50 border border-gray-200 rounded-xl px-5 py-3"
              >
                <span className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold">
                  {stat.label}
                </span>
                <span className="text-sm font-bold text-gray-800 mt-1">
                  {stat.value}
                </span>
              </div>
            ))}
          </div>

          {!showCreateButton && (
            <div className="flex flex-row justify-center mt-20">
              <button
                onClick={() =>
                  navigate(
                    `/quotation/get?id=${quotation.id}&customer=${quotation.customer}`,
                    {
                      state: { products: quotation.details },
                    },
                  )
                }
                className="bg-white text-black border font-medium text-xs px-8 py-4 rounded-lg shadow-sm hover:shadow-lg duration-300 transition-all flex flex-row items-center overflow-hidden group"
              >
                <IoCreateOutline className="text-xl w-0 opacity-0 group-hover:w-6 group-hover:opacity-100 group-hover:mr-2 transition-all duration-300 ease-in-out" />
                Create Order
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center text-4xl text-black">
          The Order you are looking for is currently unavailable
        </div>
      )}
    </main>
  );
};

export default ViewQuotation;
